"use client";

import React from "react";
import { Badge, type BadgeColor } from "@/components/ui/badge/Badge";
import {
  CaseListGradeBadge,
  normalizeCaseCheckingGrade,
} from "@/components/case-checking/caseCheckingStatusBadges";
import { getAgenticTaskStatusBadgeColor } from "@/components/missions/agenticTaskStatusBadge";
import {
  formatMeetingStatusLabel,
  getMeetingStatusBadgeColor,
} from "@/components/meetings/meetingStatusUtils";
import {
  formatMissionStatusLabel,
  getMissionStatusBadgeColor,
} from "@/components/missions/missionStatusDisplay";
import { formatStatusLabel } from "@/components/mission-control/feedEventCopy";
import type { EntityPreviewEntityType } from "./types";

function hintToStatusKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, "_");
}

function workflowStatusBadgeColor(status: string): BadgeColor {
  const key = hintToStatusKey(status);
  if (
    key === "completed" ||
    key === "complete" ||
    key === "done" ||
    key === "approved" ||
    key === "suitable"
  ) {
    return "green";
  }
  if (
    key === "needs_attention" ||
    key === "needs_review" ||
    key === "awaiting_review" ||
    key === "blocked" ||
    key === "in_progress"
  ) {
    return "amber";
  }
  if (key === "failed" || key === "error" || key === "unsuitable" || key === "unclear") {
    return "red";
  }
  if (
    key === "working" ||
    key === "running" ||
    key === "processing" ||
    key === "active"
  ) {
    return "indigo";
  }
  return "zinc";
}

function formatDetailStatusLabel(entityType: EntityPreviewEntityType, status: string): string {
  if (entityType === "mission") {
    return formatMissionStatusLabel(status);
  }
  if (entityType === "meeting") {
    return formatMeetingStatusLabel(status);
  }
  return formatStatusLabel(status);
}

type EntityPreviewStatusProps = {
  entityType: EntityPreviewEntityType;
  /** Raw status key when available (detail embeds). */
  status?: string;
  /** Display label — search hints or status_display. */
  label: string;
};

export function EntityPreviewStatus({ entityType, status, label }: EntityPreviewStatusProps) {
  const display = label.trim();
  if (!display) return null;

  if (entityType === "case_check") {
    const grade =
      normalizeCaseCheckingGrade(status) ??
      normalizeCaseCheckingGrade(display);
    if (grade) {
      return <CaseListGradeBadge grade={grade} />;
    }
  }

  const statusKey = status?.trim() ? status : hintToStatusKey(display);
  const color =
    entityType === "task"
      ? getAgenticTaskStatusBadgeColor(statusKey)
      : entityType === "mission"
        ? getMissionStatusBadgeColor(statusKey)
        : entityType === "meeting"
          ? getMeetingStatusBadgeColor(statusKey)
          : workflowStatusBadgeColor(statusKey);

  return (
    <Badge color={color} size="sm" className="shrink-0">
      {display}
    </Badge>
  );
}

export function entityPreviewDetailStatusLabel(
  entityType: EntityPreviewEntityType,
  status: string,
  statusDisplay?: string,
): string {
  if (statusDisplay?.trim()) {
    return statusDisplay.trim();
  }
  return formatDetailStatusLabel(entityType, status);
}

